// src/components/ContactDetails.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    FaSmile,
    FaMeh,
    FaFrown,
    FaSadTear,
    FaArrowLeft,
    FaSyncAlt,
    FaMagic,
    FaEllipsisV,
    FaClipboardList,
    FaEye
} from "react-icons/fa";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Tools from "../utils/Tools";
import * as auth from "../auth.js";
import tippy from 'tippy.js';


function EnhancedViewModal({ isOpen }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-xl max-w-md w-full transform transition-all animate-fadeIn">
                <div className="flex flex-col items-center">
                    <div className="w-16 h-16 mb-4 border-t-4 border-blue-600 border-solid rounded-full animate-spin"></div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-1">Generating Enhanced View</h3>
                    <p className="text-gray-600 text-center">
                        Our AI is analyzing the conversation to provide insights about agent performance and customer experience.
                    </p>
                    <p className="text-gray-800 text-center text-sm pt-5">
                        This usually takes 10-20 seconds
                    </p>
                </div>
            </div>
        </div>
    );
}

function ContactDetails() {
    const [contact, setContact] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isGeneratingEnhancedView, setIsGeneratingEnhancedView] = useState(false);
    const [showEnhancedDropdown, setShowEnhancedDropdown] = useState(false);

    const [transcriptInfo, setTranscriptInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [processedTranscriptSections, setProcessedTranscriptSections] = useState([]);
    const [isGeneratingScorecard, setIsGeneratingScorecard] = useState(false);
    const [isCategoryNoConversation, setIsCategoryNoConversation] = useState(false);

    // Reference for transcript container to enable scrolling to specific lines
    const transcriptRef = useRef(null);

    // Map to store line references for scrolling
    const lineRefs = useRef({});

    // Add this at the component level (not in the return statement):
    const enhancedViewRef = useRef(null);

    const [showBackButton, setShowBackButton] = useState(false);

    useEffect(() => {
        // Check if there's a referrer in the location state
        const hasReferrer = Boolean(location.state?.referrer);

        // Check if we have browser history to go back to
        const hasHistory = window.history.length > 1;

        // Only show the back button if we have somewhere to go back to
        setShowBackButton(hasReferrer || hasHistory);
    }, [location]);

    // Add this useEffect to handle clicks outside the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (enhancedViewRef.current && !enhancedViewRef.current.contains(event.target)) {
                setShowEnhancedDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchContactData = useCallback(() => {
        const queryParams = new URLSearchParams();
        queryParams.append("contactId", id);
        return Tools.sendRequest("get-contact", queryParams.toString()).then((data) => {
            setContact(data);
            if (data.transcriptInfo) {
                setTranscriptInfo(data.transcriptInfo);
            }
            if (data.categories?.includes("no conversation")) {
                setIsCategoryNoConversation(true);
            }
        });
    }, [id]);

    useEffect(() => {
        fetchContactData();
    }, [fetchContactData]);

    const handleGenerateEnhancedView = useCallback(() => {
        setIsGeneratingEnhancedView(true);
        Tools.sendRequest("generate-enhanced-view", {
            contactId: id
        }).then(() => {
            // Reload contact data to get the enhanced view
            fetchContactData().then(() => {
                setIsGeneratingEnhancedView(false);
            });
        }).catch((error) => {
            console.error("Error generating enhanced view:", error);
            setIsGeneratingEnhancedView(false);
        });
    }, [id, fetchContactData]);

    const handleGenerateScorecard = useCallback(async () => {
        try {
            setIsGeneratingScorecard(true);

            const requestData = {
                contactId: contact.contact_id
            };
            const scorecard_result = await Tools.sendRequest('generate-scorecard', requestData);
            const scorecard = scorecard_result.scorecard;
            console.log('Generated scorecard:', scorecard);

            // Refresh contact data to get the new scorecard_id
            await fetchContactData();

        } catch (error) {
            console.error('Failed to generate scorecard:', error);
        } finally {
            setIsGeneratingScorecard(false);
        }
    }, [contact, fetchContactData]);

    const handlePlay = async (event) => {
        const audioPlayer = event.target;
        if (isLoading || audioPlayer.src.indexOf("data:audio") === -1) {
            return;
        }

        try {
            setIsLoading(true);
            audioPlayer.pause();

            const uri =
                Tools.getRootAPI() +
                "/api?action=get-transcript-recording&contactId=" +
                encodeURIComponent(id);
            console.log("Getting WAV presigned url from this url:", uri);
            const response = await fetch(uri, {
                headers: {
                    method: "GET",
                    Authorization: window.appSettings.isOidc
                        ? undefined
                        : auth.getAccessToken(),
                },
            });
            if (response.status === 200) {
                const res = await response.json();

                audioPlayer.src = res.audioUrl;
                audioPlayer.play();
            } else {
                throw new Error("Could not fetch audio file.");
            }
        } catch (error) {
            console.error("Error playing audio:", error);
            alert("Audio file could not be played.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate(location.state?.referrer || -1);
    };

    // Function to scroll to a specific line in the transcript
    const scrollToLine = (lineNumber) => {
        if (lineRefs.current[lineNumber] && transcriptRef.current) {
            lineRefs.current[lineNumber].scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });

            // Highlight the line briefly
            lineRefs.current[lineNumber].classList.add('bg-yellow-200');
            setTimeout(() => {
                lineRefs.current[lineNumber].classList.remove('bg-yellow-200');
            }, 2000);
        }
    };

    // Function to create enhanced transcript lines with proper highlighting
    const createEnhancedLine = (text, lineNumber, lineEvidenceMap) => {
        if (!lineEvidenceMap || !lineEvidenceMap[lineNumber]) {
            return `<div id="line-${lineNumber}">${text}</div>`;
        }

        const evidence = lineEvidenceMap[lineNumber];

        // Create a map of character positions to style/explanation data
        const characterMap = new Map();

        // Process each evidence item and mark characters that need highlighting
        evidence.forEach(item => {
            if (item.quote && text.includes(item.quote)) {
                // Find the start and end positions of the quote in the text
                const startPos = text.indexOf(item.quote);
                const endPos = startPos + item.quote.length;

                // Determine highlight color based on evidence type
                let bgColorClass = '';
                switch (item.type) {
                    case 'intent': bgColorClass = 'bg-purple-100'; break;
                    case 'agent_performance': bgColorClass = 'bg-blue-100'; break;
                    case 'customer_experience': bgColorClass = 'bg-green-100'; break;
                    case 'customer_satisfaction': bgColorClass = 'bg-yellow-100'; break;
                    default: bgColorClass = ''; break;
                }

                // Add style information to each character position
                for (let i = startPos; i < endPos; i++) {
                    if (!characterMap.has(i)) {
                        characterMap.set(i, []);
                    }

                    characterMap.get(i).push({
                        bgColorClass,
                        explanation: item.explanation,
                        type: item.type,
                        intent: item.intent,
                        quote: item.quote
                    });
                }
            }
        });

        // If no specific quotes found, highlight the whole line
        if (characterMap.size === 0) {
            let bgColorClass = '';

            // Check if there are multiple types of evidence
            const evidenceTypes = new Set(evidence.map(item => item.type));

            if (evidenceTypes.size > 1) {
                bgColorClass = 'bg-red-100'; // Multiple evidence types
            } else {
                const primaryType = evidence[0].type;

                switch (primaryType) {
                    case 'intent': bgColorClass = 'bg-purple-100'; break;
                    case 'agent_performance': bgColorClass = 'bg-blue-100'; break;
                    case 'customer_experience': bgColorClass = 'bg-green-100'; break;
                    case 'customer_satisfaction': bgColorClass = 'bg-yellow-100'; break;
                    default: break;
                }
            }

            let tooltipParts = [];
            evidence.forEach(item => {
                let typeLabel = '';
                switch (item.type) {
                    case 'intent': typeLabel = `Intent (${item.intent})`; break;
                    case 'agent_performance': typeLabel = 'Agent Performance'; break;
                    case 'customer_experience': typeLabel = 'Customer Experience'; break;
                    case 'customer_satisfaction': typeLabel = 'Customer Satisfaction'; break;
                    default: typeLabel = 'Evidence'; break;
                }
                tooltipParts.push(`<span class='enh-tooltip-label'>${typeLabel}:</span><br><span class='enh-tooltip-text'>${item.explanation}</span><span class='enh-tooltip-debug'>${item.quote}</span>`);
            });

            let tooltipContent = tooltipParts.join('<br><br>');
            console.log("ttc", tooltipContent);
            return `<div id="line-${lineNumber}" class="${bgColorClass} p-1 rounded my-1" data-tippy-content="${tooltipContent.trim().replace(/"/g, '&quot;')}">${text}</div>`;
        }

        // If specific quotes found, build the line with partial highlighting
        let result = '';
        let lastPos = 0;
        let segments = [];

        // Create segments from the character map
        let currentStart = -1;
        let currentStyles = [];

        for (let i = 0; i <= text.length; i++) {
            const currentPosStyles = characterMap.get(i) || [];

            if (currentPosStyles.length === 0 && currentStart >= 0) {
                // End of a highlighted segment
                segments.push({
                    start: currentStart,
                    end: i,
                    styles: currentStyles
                });
                currentStart = -1;
                currentStyles = [];
            } else if (currentPosStyles.length > 0 && currentStart < 0) {
                // Start of a highlighted segment
                currentStart = i;
                currentStyles = currentPosStyles;
            } else if (currentPosStyles.length > 0 &&
                JSON.stringify(currentStyles) !== JSON.stringify(currentPosStyles)) {
                // Change in highlight styles
                segments.push({
                    start: currentStart,
                    end: i,
                    styles: currentStyles
                });
                currentStart = i;
                currentStyles = currentPosStyles;
            }
        }

        // Build the line with highlighted segments
        lastPos = 0;
        for (const segment of segments) {
            // Add unhighlighted text before this segment
            if (segment.start > lastPos) {
                result += text.substring(lastPos, segment.start);
            }

            // Extract content for this segment
            const content = text.substring(segment.start, segment.end);

            // Determine styling and tooltip content
            const combinedClasses = new Set();
            let tooltipParts = [];

            segment.styles.forEach(style => {
                combinedClasses.add(style.bgColorClass);

                let typeLabel = '';
                switch (style.type) {
                    case 'intent': typeLabel = `Intent (${style.intent})`; break;
                    case 'agent_performance': typeLabel = 'Agent Performance'; break;
                    case 'customer_experience': typeLabel = 'Customer Experience'; break;
                    case 'customer_satisfaction': typeLabel = 'Customer Satisfaction'; break;
                    default: typeLabel = 'Evidence'; break;
                }

                tooltipParts.push(`<span class='enh-tooltip-label'>${typeLabel}:</span><br><span class='enh-tooltip-text'>${style.explanation}</span><span class='enh-tooltip-debug'>${style.quote}</span>`);
            });

            let tooltipContent = tooltipParts.join('<br><br>');
            // Apply multiple evidence class if there are different types of evidence
            let classNames = combinedClasses.size > 1 ? 'bg-red-100' : Array.from(combinedClasses).join(' ');

            // Add highlighted segment with tooltip
            result += `<span class="${classNames} p-1 rounded" data-tippy-content="${tooltipContent.trim().replace(/"/g, '&quot;')}">${content}</span>`;

            lastPos = segment.end;
        }

        // Add any remaining text after the last segment
        if (lastPos < text.length) {
            result += text.substring(lastPos);
        }

        return `<div id="line-${lineNumber}">${result}</div>`;
    };

    // Helper function to process a text transcript with enhanced view highlighting
    const processEnhancedTextTranscript = (content, enhancedView) => {
        if (!content || !enhancedView) return content;

        // Split the content into lines while preserving line breaks
        const lines = content.split('\n');

        // Create a map of line numbers to evidence categories
        const lineEvidenceMap = {};

        // Process intent evidence
        if (enhancedView.intents_evidence) {
            Object.keys(enhancedView.intents_evidence).forEach(intent => {
                enhancedView.intents_evidence[intent].forEach(evidence => {
                    if (!lineEvidenceMap[evidence.line]) {
                        lineEvidenceMap[evidence.line] = [];
                    }
                    lineEvidenceMap[evidence.line].push({
                        type: 'intent',
                        intent: intent,
                        quote: evidence.quote,
                        explanation: evidence.explanation
                    });
                });
            });
        }

        // Process agent performance evidence
        if (enhancedView.agent_performance_evidence) {
            enhancedView.agent_performance_evidence.forEach(evidence => {
                if (!lineEvidenceMap[evidence.line]) {
                    lineEvidenceMap[evidence.line] = [];
                }
                lineEvidenceMap[evidence.line].push({
                    type: 'agent_performance',
                    quote: evidence.quote,
                    explanation: evidence.explanation
                });
            });
        }

        // Process customer experience evidence
        if (enhancedView.customer_experience_evidence) {
            enhancedView.customer_experience_evidence.forEach(evidence => {
                if (!lineEvidenceMap[evidence.line]) {
                    lineEvidenceMap[evidence.line] = [];
                }
                lineEvidenceMap[evidence.line].push({
                    type: 'customer_experience',
                    quote: evidence.quote,
                    explanation: evidence.explanation
                });
            });
        }

        // Process customer satisfaction evidence
        if (enhancedView.customer_final_satisfaction_evidence) {
            enhancedView.customer_final_satisfaction_evidence.forEach(evidence => {
                if (!lineEvidenceMap[evidence.line]) {
                    lineEvidenceMap[evidence.line] = [];
                }
                lineEvidenceMap[evidence.line].push({
                    type: 'customer_satisfaction',
                    quote: evidence.quote,
                    explanation: evidence.explanation
                });
            });
        }

        // Process each line using the createEnhancedLine function
        const processedLines = lines.map((line, index) => {
            const lineNumber = index + 1;
            return createEnhancedLine(line, lineNumber, lineEvidenceMap);
        });

        return processedLines.join('\n');
    };

    // Process the transcript with overlap periods and enhanced view
    useEffect(() => {
        if (!contact || !transcriptInfo) return;

        // Function to process transcript with both overlap periods and enhanced view
        const processTranscriptWithOverlapsAndEnhanced = () => {
            // For text type transcript with enhanced view
            if (transcriptInfo.type === "text") {
                const enhancedView = contact.enhanced_view_json
                    ? (typeof contact.enhanced_view_json === 'string'
                        ? JSON.parse(contact.enhanced_view_json)
                        : contact.enhanced_view_json)
                    : null;

                if (enhancedView) {
                    // Process the transcript with enhanced view highlights
                    const processedContent = processEnhancedTextTranscript(
                        transcriptInfo.content,
                        enhancedView
                    );

                    setProcessedTranscriptSections([
                        {
                            type: "normal",
                            content: processedContent,
                            isEnhanced: true
                        },
                    ]);
                } else {
                    // Regular transcript without enhanced view
                    setProcessedTranscriptSections([
                        {
                            type: "normal",
                            content: transcriptInfo.content,
                        },
                    ]);
                }
                return;
            }

            // For JSON structured transcript
            if (transcriptInfo.type === "json" && transcriptInfo.content) {
                const data = transcriptInfo.content;
                if (!data.Transcript || !Array.isArray(data.Transcript)) {
                    setProcessedTranscriptSections([{ type: "normal", content: "" }]);
                    return;
                }

                // Track all overlap periods
                const overlapPeriods = [];

                // Process next call overlaps
                if (contact.next_linked_calls_details?.length > 0) {
                    const nextCall = contact.next_linked_calls_details.find(
                        (call) =>
                            call.transfer_type === "consult" ||
                            call.transfer_type === "warm_transfer"
                    );

                    if (nextCall && nextCall.finalEndOffsetMillis) {
                        const nextCallDate = new Date(nextCall.call_date);
                        const currentCallDate = new Date(contact.call_date);

                        const offsetMs = 2000; // Compensation for timing issues
                        const overlapStartMs = nextCallDate - currentCallDate - offsetMs;

                        let overlapEndMs;
                        if (nextCall.transfer_type === "consult") {
                            // For consult: next call ends before current call
                            overlapEndMs = overlapStartMs + nextCall.finalEndOffsetMillis;
                        } else {
                            // warm_transfer
                            // For warm transfer: current call ends before next call
                            overlapEndMs = contact.duration * 1000;
                        }

                        overlapPeriods.push({
                            start: overlapStartMs,
                            end: overlapEndMs,
                            type: "next",
                            transferType: nextCall.transfer_type,
                        });
                    }
                }

                // Process previous call overlaps
                if (contact.prev_linked_calls_details?.length > 0) {
                    const prevCall = contact.prev_linked_calls_details.find(
                        (call) =>
                            call.transfer_type === "consult" ||
                            call.transfer_type === "warm_transfer"
                    );

                    if (prevCall && prevCall.finalEndOffsetMillis) {
                        const prevCallDate = new Date(prevCall.call_date);
                        const currentCallDate = new Date(contact.call_date);
                        const overlapDuration =
                            prevCallDate.getTime() +
                            prevCall.finalEndOffsetMillis -
                            currentCallDate.getTime();

                        if (overlapDuration > 0) {
                            overlapPeriods.push({
                                start: 0, // From the start of current call
                                end: overlapDuration,
                                type: "prev",
                                transferType: prevCall.transfer_type,
                            });
                        }
                    }
                }

                // Check if previous call was a consult
                const isPrevConsult = contact.prev_linked_calls_details?.some(
                    (call) => call.transfer_type === "consult"
                );

                // Enhanced view processing
                const enhancedView = contact.enhanced_view_json
                    ? (typeof contact.enhanced_view_json === 'string'
                        ? JSON.parse(contact.enhanced_view_json)
                        : contact.enhanced_view_json)
                    : null;

                // Create a map of line numbers to evidence categories
                const lineEvidenceMap = {};
                if (enhancedView) {
                    // Process intent evidence
                    if (enhancedView.intents_evidence) {
                        Object.keys(enhancedView.intents_evidence).forEach(intent => {
                            enhancedView.intents_evidence[intent].forEach(evidence => {
                                if (!lineEvidenceMap[evidence.line]) {
                                    lineEvidenceMap[evidence.line] = [];
                                }
                                lineEvidenceMap[evidence.line].push({
                                    type: 'intent',
                                    intent: intent,
                                    quote: evidence.quote,
                                    explanation: evidence.explanation
                                });
                            });
                        });
                    }

                    // Process agent performance evidence
                    if (enhancedView.agent_performance_evidence) {
                        enhancedView.agent_performance_evidence.forEach(evidence => {
                            if (!lineEvidenceMap[evidence.line]) {
                                lineEvidenceMap[evidence.line] = [];
                            }
                            lineEvidenceMap[evidence.line].push({
                                type: 'agent_performance',
                                quote: evidence.quote,
                                explanation: evidence.explanation
                            });
                        });
                    }

                    // Process customer experience evidence
                    if (enhancedView.customer_experience_evidence) {
                        enhancedView.customer_experience_evidence.forEach(evidence => {
                            if (!lineEvidenceMap[evidence.line]) {
                                lineEvidenceMap[evidence.line] = [];
                            }
                            lineEvidenceMap[evidence.line].push({
                                type: 'customer_experience',
                                quote: evidence.quote,
                                explanation: evidence.explanation
                            });
                        });
                    }

                    // Process customer satisfaction evidence
                    if (enhancedView.customer_final_satisfaction_evidence) {
                        enhancedView.customer_final_satisfaction_evidence.forEach(evidence => {
                            if (!lineEvidenceMap[evidence.line]) {
                                lineEvidenceMap[evidence.line] = [];
                            }
                            lineEvidenceMap[evidence.line].push({
                                type: 'customer_satisfaction',
                                quote: evidence.quote,
                                explanation: evidence.explanation
                            });
                        });
                    }
                }

                const silenceThresholdMs = 3 * 1000;
                const transcript = data.Transcript;

                // Create a timeline tracking when speaking starts and ends
                const timeline = [];
                for (const item of transcript) {
                    timeline.push({
                        time: item.BeginOffsetMillis,
                        type: "start",
                        item,
                    });

                    timeline.push({
                        time: item.EndOffsetMillis,
                        type: "end",
                    });
                }

                // Sort timeline chronologically
                timeline.sort((a, b) => {
                    if (a.time === b.time) {
                        return a.type === "end" ? -1 : 1;
                    }
                    return a.time - b.time;
                });

                // Find silence periods
                let speakingCount = 0;
                let silenceStartTime = null;
                const silencePeriods = [];

                for (let i = 0; i < timeline.length; i++) {
                    const event = timeline[i];

                    if (event.type === "start") {
                        if (speakingCount === 0 && silenceStartTime !== null) {
                            const silenceDuration = event.time - silenceStartTime;
                            if (silenceDuration >= silenceThresholdMs) {
                                silencePeriods.push({
                                    start: silenceStartTime,
                                    duration: silenceDuration,
                                });
                            }
                        }
                        speakingCount++;
                    } else {
                        speakingCount--;
                        if (speakingCount === 0) {
                            silenceStartTime = event.time;
                        }
                    }
                }

                // Create final timeline with both transcript entries and silence markers
                const finalTimeline = [
                    ...transcript.map((item) => ({
                        time: item.BeginOffsetMillis,
                        type: "transcript",
                        item,
                        lineNumber: item.LineId || null,
                    })),
                    ...silencePeriods.map((silence, idx) => ({
                        time: silence.start,
                        type: "silence",
                        duration: silence.duration,
                        lineNumber: null,
                    })),
                ];

                finalTimeline.sort((a, b) => a.time - b.time);

                // Assign proper line numbers
                let lineCounter = 0;
                finalTimeline.forEach(event => {
                    if (event.type !== 'silence') {
                        event.lineNumber = lineCounter++;
                    } else {
                        event.lineNumber = -1;
                    }
                });

                // Process into sections (normal and overlap)
                const sections = [];
                let currentSection = { type: "normal", content: "" };

                for (const event of finalTimeline) {
                    const timestamp = formatTimestamp2(event.time);

                    // Find if this event is in any overlap period
                    let activeOverlap = null;
                    for (const period of overlapPeriods) {
                        if (event.time >= period.start && event.time <= period.end) {
                            activeOverlap = period;
                            break;
                        }
                    }

                    // Check if we need to start a new section
                    if (activeOverlap && currentSection.type === "normal") {
                        // Save current normal section
                        if (currentSection.content.trim()) {
                            sections.push(currentSection);
                        }
                        // Start new overlap section
                        currentSection = {
                            type: "overlap",
                            overlapType: activeOverlap.type,
                            transferType: activeOverlap.transferType,
                            content: "",
                        };
                    } else if (!activeOverlap && currentSection.type === "overlap") {
                        // Save current overlap section
                        if (currentSection.content.trim()) {
                            sections.push(currentSection);
                        }
                        // Start new normal section
                        currentSection = { type: "normal", content: "" };
                    } else if (
                        activeOverlap &&
                        currentSection.type === "overlap" &&
                        (currentSection.overlapType !== activeOverlap.type ||
                            currentSection.transferType !== activeOverlap.transferType)
                    ) {
                        // If we're switching between different types of overlap
                        if (currentSection.content.trim()) {
                            sections.push(currentSection);
                        }
                        currentSection = {
                            type: "overlap",
                            overlapType: activeOverlap.type,
                            transferType: activeOverlap.transferType,
                            content: "",
                        };
                    }

                    // Add the event to the current section
                    if (event.type === "transcript") {
                        let speaker =
                            event.item.ParticipantId === "AGENT" ? "Agent" : "Customer";

                        // Handle speaker relabeling based on overlap type and previous consult status
                        if (activeOverlap) {
                            if (activeOverlap.type === "next" && speaker === "Customer") {
                                speaker = "Second Agent";
                            } else if (
                                activeOverlap.type === "prev" &&
                                speaker === "Customer"
                            ) {
                                speaker = "Previous Agent";
                            }
                        } else if (isPrevConsult && speaker === "Customer") {
                            // Legacy behavior for previous consult - applies to the entire call
                            speaker = "Previous Agent";
                        }

                        const content = event.item.Content || "";

                        // Apply enhanced view highlighting if available
                        if (enhancedView && lineEvidenceMap[event.lineNumber]) {
                            currentSection.content += createEnhancedLine(
                                `<span class='enh-timestamp'>${timestamp}</span>   <span class='enh-speaker'>${speaker}:</span> ${content}`,
                                event.lineNumber,
                                lineEvidenceMap
                            ) + '\n';
                        } else {
                            currentSection.content += `<div id="line-${event.lineNumber}"><span class='enh-timestamp'>${timestamp}</span>   <span class='enh-speaker'>${speaker}:</span> ${content}</div>\n`;
                        }

                    } else if (event.type === "silence") {
                        const silenceSeconds = Math.floor(event.duration / 1000);
                        currentSection.content += `<div id="line-${event.lineNumber}" class="silence-line"><span class='enh-timestamp'>${timestamp}</span><span class='enh-silence'>   [SILENCE - ${silenceSeconds} seconds]</span></div>\n`;
                    }
                }

                // Add the last section if not empty
                if (currentSection.content.trim()) {
                    sections.push(currentSection);
                }

                // Mark sections with enhanced content
                if (enhancedView) {
                    sections.forEach(section => {
                        if (section.type === "normal") {
                            section.isEnhanced = true;
                        }
                    });
                }

                setProcessedTranscriptSections(sections);
            }
        };

        processTranscriptWithOverlapsAndEnhanced();

    }, [contact, transcriptInfo]);


    if (!contact) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="loader"></div>
            </div>
        );
    }

    function formatTimestamp2(milliseconds) {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        // Format as mm:ss
        return `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`;
    }

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        let result = "";

        if (hours > 0) {
            result += hours + (hours === 1 ? " hour " : " hours ");
        }

        if (minutes > 0 || (hours > 0 && (remainingSeconds > 0 || minutes === 0))) {
            result += minutes + (minutes === 1 ? " minute " : " minutes ");
        }

        if (
            remainingSeconds > 0 ||
            (hours === 0 && minutes === 0 && remainingSeconds === 0)
        ) {
            result +=
                remainingSeconds + (remainingSeconds === 1 ? " second" : " seconds");
        }

        return result.trim();
    };

    const formatTimestamp = (date) => {
        return new Date(date).toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        });
    };

    // Function to get a small sentiment icon
    const getSentimentIconSmall = (sentiment, label) => {
        const sentimentValue = parseFloat(sentiment);
        let icon;
        let color;

        if (isNaN(sentimentValue)) {
            icon = <FaMeh size="1.5em" />;
            color = "text-gray-300";
        } else if (sentimentValue > 0.75) {
            icon = <FaSmile size="1.5em" />;
            color = "text-green-400";
        } else if (sentimentValue > 0.25) {
            icon = <FaSmile size="1.5em" />;
            color = "text-green-250";
        } else if (sentimentValue >= -0.25) {
            icon = <FaMeh size="1.5em" />;
            color = "text-gray-300";
        } else if (sentimentValue >= -0.75) {
            icon = <FaFrown size="1.5em" />;
            color = "text-red-300";
        } else {
            icon = <FaSadTear size="1.5em" />;
            color = "text-red-500";
        }

        return (
            <Tippy
                content={`${label}: ${isNaN(sentimentValue) ? "Unknown" : sentimentValue.toFixed(2)
                    }`}
            >
                <span className={`ml-2 ${color}`}>{icon}</span>
            </Tippy>
        );
    };

    // Function to find the first line for a specific evidence type
    const findFirstLineForEvidence = (evidenceType) => {
        if (!contact.enhanced_view_json) return null;

        const enhancedView = typeof contact.enhanced_view_json === 'string'
            ? JSON.parse(contact.enhanced_view_json)
            : contact.enhanced_view_json;

        let firstLine = null;

        switch (evidenceType) {
            case 'agent_performance':
                if (enhancedView.agent_performance_evidence && enhancedView.agent_performance_evidence.length > 0) {
                    firstLine = enhancedView.agent_performance_evidence[0].line;
                }
                break;
            case 'customer_experience':
                if (enhancedView.customer_experience_evidence && enhancedView.customer_experience_evidence.length > 0) {
                    firstLine = enhancedView.customer_experience_evidence[0].line;
                }
                break;
            case 'customer_satisfaction':
                if (enhancedView.customer_final_satisfaction_evidence &&
                    enhancedView.customer_final_satisfaction_evidence.length > 0) {
                    firstLine = enhancedView.customer_final_satisfaction_evidence[0].line;
                }
                break;
            default:
                break;
        }

        return firstLine;
    };

    // Function to get labels and colors based on score, now with clickable behavior
    const getLabelAndColor = (score, field) => {
        const value = parseFloat(score);

        if (isNaN(value)) {
            return (
                <Tippy content="Score: N/A">
                    <span className="text-gray-500 ml-1">N/A</span>
                </Tippy>
            );
        }

        let label = "";
        let color = "";

        if (field === "performance") {
            if (value > 0.8) {
                label = "Excellent";
                color = "text-green-700";
            } else if (value > 0.6) {
                label = "Good";
                color = "text-green-500";
            } else if (value >= 0.4) {
                label = "Average";
                color = "text-yellow-500";
            } else if (value >= 0.2) {
                label = "Needs Improvement";
                color = "text-orange-500";
            } else {
                label = "Unsatisfactory";
                color = "text-red-600";
            }
        } else if (field === "experience") {
            if (value > 0.8) {
                label = "Very Positive";
                color = "text-green-600";
            } else if (value > 0.6) {
                label = "Positive";
                color = "text-green-400";
            } else if (value >= 0.4) {
                label = "Neutral";
                color = "text-gray-400";
            } else if (value >= 0.2) {
                label = "Negative";
                color = "text-red-400";
            } else {
                label = "Very Negative";
                color = "text-red-700";
            }
        } else if (field === "satisfaction") {
            if (value > 0.8) {
                label = "Very Satisfied";
                color = "text-green-600";
            } else if (value > 0.6) {
                label = "Satisfied";
                color = "text-green-500";
            } else if (value >= 0.4) {
                label = "Neutral";
                color = "text-gray-400";
            } else if (value >= 0.2) {
                label = "Dissatisfied";
                color = "text-red-400";
            } else {
                label = "Very Dissatisfied";
                color = "text-red-700 font-semibold";
            }
        } else {
            label = value.toFixed(2);
            color = "text-gray-500";
        }

        // If enhanced view is available, make the label clickable
        if (contact.enhanced_view_json && false) {
            const firstLine = findFirstLineForEvidence(field === 'performance' ? 'agent_performance' :
                field === 'experience' ? 'customer_experience' :
                    'customer_satisfaction');

            if (firstLine) {
                return (
                    <Tippy content={`Score: ${value.toFixed(2)} - Click to view evidence in transcript`}>
                        <span
                            className={`${color} ml-1 cursor-pointer hover:underline`}
                            onClick={() => scrollToLine(firstLine)}
                        >
                            {label}
                        </span>
                    </Tippy>
                );
            }
        }

        // Default non-clickable version
        return (
            <Tippy content={`Score: ${value.toFixed(2)}`}>
                <span className={`${color} ml-1`}>{label}</span>
            </Tippy>
        );
    };

    // Parse the percentages and ensure they are numbers
    const agentPercent = parseFloat(contact.agent_percent) || 0;
    const userPercent = parseFloat(contact.user_percent) || 0;
    const silencePercent = parseFloat(contact.silence_percent) || 0;

    const isTranscriptTooShort = () => {
        if (!transcriptInfo || !transcriptInfo.content) return true;

        if ('Transcript' in transcriptInfo.content) {
            return transcriptInfo.content.Transcript.length < 5;
        }
        if (typeof transcriptInfo.content == 'string') {
            const contentLength = transcriptInfo.content.length;
            const lineCount = transcriptInfo.content.split('\n').length;

            return contentLength < 100 || lineCount < 5;
        }

        console.warn("Unknown TranscriptInfo", transcriptInfo)

        return false;
    };

    // Render the enhanced transcript with highlighted sections
    const renderEnhancedTranscript = (section) => {
        return (
            <div
                className="usan-transcript-box font-sans"
                dangerouslySetInnerHTML={{ __html: section.content }}
                ref={el => {
                    if (el && section.isEnhanced) {
                        // Initialize tippy.js tooltips
                        if (tippy) {
                            tippy('[data-tippy-content]', {
                                allowHTML: true,
                                placement: 'top',
                                arrow: true,
                                theme: 'light-border',
                                maxWidth: 300
                            });
                        }

                        // Store line references for scrolling
                        el.querySelectorAll('[id^="line-"]').forEach(lineEl => {
                            const lineNum = parseInt(lineEl.id.replace('line-', ''));
                            lineRefs.current[lineNum] = lineEl;
                        });
                    }
                }}
            />
        );
    };

    return (
        <div className="container mx-auto p-6">
            <EnhancedViewModal isOpen={isGeneratingEnhancedView} />

            {showBackButton && (
                <button
                    className="text-gray-600 hover:text-gray-800 mb-4 flex items-center"
                    onClick={handleBackClick}
                >
                    <FaArrowLeft className="mr-1" /> Back
                </button>
            )}

            {/* Header with Enhanced View and Scorecard Buttons */}
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-4xl font-bold">Contact Details</h1>

                <div className="flex items-center gap-4">
                    {/* Scorecard Buttons - Only show if Quality Management is enabled */}
                    {window.appSettings?.qualityManagementEnabled && (
                        <>
                            {contact.scorecard_id ? (
                                <Tippy content="View the generated scorecard for this contact">
                                    <button
                                        onClick={() => navigate(`/scorecard/${contact.contact_id}`)}
                                        onContextMenu={(event) => {
                                            event.preventDefault();

                                            // Remove any existing menu to prevent multiple menus
                                            const existingMenu = document.getElementById("custom-menu");
                                            if (existingMenu) {
                                                document.body.removeChild(existingMenu);
                                            }

                                            // Create the menu
                                            const menu = document.createElement("div");
                                            menu.id = "custom-menu";
                                            menu.style.position = "absolute";
                                            menu.style.left = `${event.pageX}px`;
                                            menu.style.top = `${event.pageY}px`;
                                            menu.style.border = "1px solid #ccc";
                                            menu.style.padding = "8px";
                                            menu.style.zIndex = 1000;
                                            menu.style.backgroundColor = "white";
                                            menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
                                            menu.style.borderRadius = "4px";

                                            // Create the "Open in New Window" option
                                            const openInNewWindowOption = document.createElement("div");
                                            openInNewWindowOption.textContent = "Open in New Window";
                                            openInNewWindowOption.style.cursor = "pointer";
                                            openInNewWindowOption.style.padding = "4px 8px";
                                            openInNewWindowOption.onmouseover = () => {
                                                openInNewWindowOption.style.backgroundColor = "#f3f4f6";
                                            };
                                            openInNewWindowOption.onmouseout = () => {
                                                openInNewWindowOption.style.backgroundColor = "transparent";
                                            };
                                            openInNewWindowOption.onclick = () => {
                                                window.open(`/scorecard/${contact.contact_id}`, "_blank");
                                                if (menu.parentNode) {
                                                    menu.parentNode.removeChild(menu);
                                                }
                                            };

                                            menu.appendChild(openInNewWindowOption);
                                            document.body.appendChild(menu);

                                            // Remove the menu when clicking outside
                                            const handleClickOutside = (event) => {
                                                if (menu && !menu.contains(event.target)) {
                                                    if (menu.parentNode) {
                                                        menu.parentNode.removeChild(menu);
                                                    }
                                                    document.removeEventListener("click", handleClickOutside);
                                                }
                                            };

                                            document.addEventListener("click", handleClickOutside);
                                        }}
                                        className="flex items-center bg-green-600 text-white hover:bg-green-700 py-2 px-4 rounded-md transition"
                                    >
                                        <FaEye className="mr-2" />
                                        View Scorecard
                                    </button>
                                </Tippy>
                            ) : (
                                <Tippy content={isCategoryNoConversation ?
                                    "You cannot generate a scorecard for a contact where no conversation has occurred." :
                                    "Generate a quality scorecard for this contact using AI analysis"}>
                                    <span className={isCategoryNoConversation ? "cursor-not-allowed" : ""}>
                                        <button
                                            onClick={handleGenerateScorecard}
                                            disabled={isGeneratingScorecard || isCategoryNoConversation}
                                            className={`flex items-center bg-purple-600 text-white hover:bg-purple-700 py-2 px-4 rounded-md transition ${isGeneratingScorecard || isCategoryNoConversation ? "opacity-70 cursor-not-allowed" : ""
                                                }`}
                                        >
                                            {isGeneratingScorecard ? (
                                                <>
                                                    <span className="mr-2 animate-spin">⟳</span>
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <FaClipboardList className="mr-2" />
                                                    Generate Scorecard
                                                </>
                                            )}
                                        </button>
                                    </span>
                                </Tippy>
                            )}
                        </>
                    )}

                    {/* Enhanced View Button */}
                    {!contact.enhanced_view_json ? (
                        <Tippy
                            content={isTranscriptTooShort() ?
                                "Enhanced view requires a longer transcript to generate meaningful insights." :
                                "Use GenAI to process transcript and analyze the source of intents, agent professionalism and customer experience"
                            }
                        >
                            <span className={isTranscriptTooShort() ? "cursor-not-allowed" : ""}>
                                <button
                                    onClick={handleGenerateEnhancedView}
                                    disabled={isGeneratingEnhancedView || isTranscriptTooShort()}
                                    className={`flex items-center bg-blue-600 text-white hover:bg-blue-700 py-2 px-4 rounded-md transition ${(isGeneratingEnhancedView || isTranscriptTooShort()) ? "opacity-70 cursor-not-allowed" : ""
                                        }`}
                                >
                                    {isGeneratingEnhancedView ? (
                                        <>
                                            <span className="mr-2 animate-spin">⟳</span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaMagic className="mr-2" />
                                            Generate Enhanced View
                                        </>
                                    )}
                                </button>
                            </span>
                        </Tippy>
                    ) : (
                        <div className="relative" ref={enhancedViewRef}>
                            <div className="flex items-center bg-blue-100 text-blue-800 py-2 px-4 rounded-md">
                                <div className="flex flex-col items-start flex-grow">
                                    <div className="flex items-center">
                                        <FaMagic className="mr-2" />
                                        <span>Enhanced View</span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        {new Date(contact.enhanced_view_created_date).toLocaleString()}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setShowEnhancedDropdown(!showEnhancedDropdown)}
                                    className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-200 transition-colors"
                                    aria-label="More options"
                                >
                                    <FaEllipsisV />
                                </button>
                            </div>

                            {showEnhancedDropdown && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                    <button
                                        onClick={() => {
                                            handleGenerateEnhancedView();
                                            setShowEnhancedDropdown(false);
                                        }}
                                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <FaSyncAlt className="mr-2" /> Regenerate
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Call Information Card */}
                <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                    <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                    <p className="mb-2">
                        <strong>Date:</strong> {formatTimestamp(contact.call_date)}
                    </p>
                    <p className="mb-2">
                        <strong>Duration:</strong> {formatTime(contact.duration)}
                    </p>
                    <p className="mb-2">
                        <strong>Contact ID:</strong> {contact.contact_id}
                    </p>
                    <p className="mb-2">
                        <strong>ANI:</strong> {contact.ani}
                    </p>
                    <p className="mb-2">
                        <strong>Origination:</strong> {contact.initiation_method}
                    </p>
                    <p className="mb-2">
                        <strong>Queue:</strong> {contact.queue}
                    </p>

                    <p className="mb-2">
                        <strong>Categories:</strong>
                        <span className="ml-2">
                            {contact.categories &&
                                contact.categories.map((category, index) => (
                                    <span
                                        key={index}
                                        className="bg-green-100 text-green-800 text-xs font-medium mr-2 mb-2 px-2.5 py-0.5 rounded inline-block"
                                    >
                                        {category ? category.trim() : "None"}
                                    </span>
                                ))}
                        </span>
                    </p>

                    <p className="mb-2">
                        <strong>Intents:</strong>
                        <span className="ml-2">
                            {contact.intents_agg_json &&
                                contact.intents_agg_json.map((intent, index) => {
                                    // Find first line of evidence for this intent if available
                                    let firstLine = null;
                                    if (contact.enhanced_view_json && false) {
                                        const enhancedView = typeof contact.enhanced_view_json === 'string'
                                            ? JSON.parse(contact.enhanced_view_json)
                                            : contact.enhanced_view_json;

                                        if (enhancedView.intents_evidence && enhancedView.intents_evidence[intent.major_intent]) {
                                            firstLine = enhancedView.intents_evidence[intent.major_intent][0]?.line;
                                        }
                                    }

                                    return (
                                        <Tippy
                                            key={index}
                                            content={
                                                <div>
                                                    <div>
                                                        <span className="text-xs text-gray-400">
                                                            Category:{" "}
                                                        </span>
                                                        {intent.category}
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-400">
                                                            Major Intent:{" "}
                                                        </span>
                                                        {intent.major_intent}
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-400">
                                                            Minor Intent:{" "}
                                                        </span>
                                                        {intent.minor_intent}
                                                    </div>
                                                    {firstLine && (
                                                        <div className="mt-1 text-xs text-blue-500">
                                                            Click to see evidence in transcript
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <span
                                                className={`bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded ${firstLine ? 'cursor-pointer hover:bg-blue-200' : ''}`}
                                                onClick={() => firstLine && scrollToLine(firstLine)}
                                            >
                                                {intent.major_intent}
                                            </span>
                                        </Tippy>
                                    );
                                })}
                        </span>
                    </p>

                    <p className="mb-2">
                        <strong>Summary:</strong> {contact.summary}
                    </p>
                </div>

                {/* Agent Card */}
                <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                    <h2 className="text-xl font-semibold mb-4">Agent Information</h2>
                    <p className="mb-2 flex items-center">
                        <strong>Agent Name:</strong>
                        <span className="ml-2">{contact.agent}</span>
                    </p>
                    <p className="mb-2 flex items-center">
                        <strong>Sentiment:</strong>{" "}
                        {getSentimentIconSmall(contact.agent_sentiment, "Agent Sentiment")}
                    </p>
                    <p className="mb-1">
                        <strong>Performance:</strong>{" "}
                        {getLabelAndColor(contact.agent_performance_score, "performance")}
                    </p>
                    <p className="text-gray-500 text-sm mb-6">
                        {contact.agent_performance_reason}
                    </p>
                </div>

                {/* Customer Card */}
                <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-green-50 to-green-100">
                    <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
                    <p className="mb-2 flex items-center">
                        <strong>Sentiment:</strong>{" "}
                        {getSentimentIconSmall(
                            contact.customer_sentiment,
                            "Customer Sentiment"
                        )}
                    </p>
                    <p className="mb-2">
                        <strong>Experience:</strong>{" "}
                        {getLabelAndColor(contact.customer_experience_score, "experience")}
                    </p>
                    <p className="text-gray-500 text-sm mb-2">
                        {contact.customer_experience_score_reason}
                    </p>
                    <p className="mb-2">
                        <strong>Satisfaction:</strong>{" "}
                        {getLabelAndColor(
                            contact.customer_final_satisfaction,
                            "satisfaction"
                        )}
                    </p>
                    <p className="text-gray-500 text-sm">
                        {contact.customer_final_satisfaction_reason}
                    </p>
                </div>
            </div>

            {/* Conversation Participation Bar */}
            {agentPercent != null &&
                userPercent != null &&
                silencePercent != null && (
                    <div className="mt-8 mb-4 h-5 relative rounded overflow-hidden">
                        <div className="flex h-full">
                            {agentPercent != null && (
                                <div
                                    style={{
                                        width: agentPercent === 0 ? "4px" : `${agentPercent}%`,
                                        backgroundColor: "#4A90E2",
                                    }}
                                    className="h-full relative"
                                >
                                    <Tippy
                                        content={`Agent Talk Time: ${agentPercent.toFixed(1)}%`}
                                    >
                                        <span className="absolute inset-0"></span>
                                    </Tippy>
                                </div>
                            )}
                            {userPercent != null && (
                                <div
                                    style={{
                                        width: userPercent === 0 ? "4px" : `${userPercent}%`,
                                        backgroundColor: "#50E3C2",
                                    }}
                                    className="h-full relative"
                                >
                                    <Tippy
                                        content={`Customer Talk Time: ${userPercent.toFixed(1)}%`}
                                    >
                                        <span className="absolute inset-0"></span>
                                    </Tippy>
                                </div>
                            )}
                            {silencePercent != null && (
                                <div
                                    style={{
                                        width: silencePercent === 0 ? "4px" : `${silencePercent}%`,
                                        backgroundColor: "#9B9B9B",
                                    }}
                                    className="h-full relative"
                                >
                                    <Tippy
                                        content={`Silence Time: ${silencePercent.toFixed(1)}%`}
                                    >
                                        <span className="absolute inset-0"></span>
                                    </Tippy>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Previous Calls */}
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-2">Previous Contacts</h2>
                    <div className="flex flex-col gap-2">
                        {(() => {
                            // Track processed IDs to avoid duplicates
                            const processedIds = new Set();
                            let output = [];

                            // First process detailed call records
                            if (contact.prev_linked_calls_details && contact.prev_linked_calls_details.length > 0) {
                                contact.prev_linked_calls_details.forEach((prevCall, index) => {
                                    processedIds.add(prevCall.contact_id);
                                    output.push(
                                        <a
                                            key={`prev-detail-${index}`}
                                            href={`/contact/${prevCall.contact_id}`}
                                            className="inline-flex items-center justify-between text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded w-full"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-blue-900">
                                                    {formatTimestamp(prevCall.call_date)}
                                                </span>
                                                <span className="text-xs text-blue-700">
                                                    {formatTime(prevCall.duration)}
                                                </span>
                                            </div>
                                            <span className="ml-2 text-xs bg-blue-200 px-1 rounded whitespace-nowrap">
                                                {prevCall.initiation_method}
                                            </span>
                                        </a>
                                    );
                                });
                            }

                            // Add specific prev_contact_id if not already processed
                            if (contact.prev_contact_id && !processedIds.has(contact.prev_contact_id)) {
                                processedIds.add(contact.prev_contact_id);
                                const hasDetails = contact.specific_prev_contact_call_date &&
                                    contact.specific_prev_contact_duration !== null &&
                                    contact.specific_prev_contact_initiation_method;

                                if (hasDetails) {
                                    output.push(
                                        <a
                                            key="prev-specific"
                                            href={`/contact/${contact.prev_contact_id}`}
                                            className="inline-flex items-center justify-between text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded w-full"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-blue-900">
                                                    {new Date(contact.specific_prev_contact_call_date).toLocaleString()}
                                                </span>
                                                <span className="text-xs text-blue-700">
                                                    {formatTime(contact.specific_prev_contact_duration)}
                                                </span>
                                            </div>
                                            <span className="ml-2 text-xs bg-blue-200 px-1 rounded whitespace-nowrap">
                                                {contact.specific_prev_contact_initiation_method}
                                            </span>
                                        </a>
                                    );
                                } else {
                                    // External contact without details
                                    output.push(
                                        <div
                                            key="prev-external"
                                            className="inline-flex items-center justify-between text-sm bg-gray-100 text-gray-500 px-3 py-2 rounded w-full cursor-text"
                                        >
                                            <span className="overflow-hidden text-ellipsis">
                                                {contact.prev_contact_id}
                                            </span>
                                            <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">
                                                External
                                            </span>
                                        </div>
                                    );
                                }
                            }

                            // Legacy format as last resort
                            if (output.length === 0 && contact.prev_linked_contact_ids) {
                                const linkedIds = contact.prev_linked_contact_ids.split('|');
                                const linkedMethods = contact.prev_linked_initiation_methods ?
                                    contact.prev_linked_initiation_methods.split('|') : [];

                                linkedIds.forEach((prevId, index) => {
                                    if (!processedIds.has(prevId)) {
                                        processedIds.add(prevId);
                                        output.push(
                                            <a
                                                key={`prev-legacy-${index}`}
                                                href={`/contact/${prevId}`}
                                                className="inline-flex items-center justify-between text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded w-full"
                                            >
                                                <span className="overflow-hidden text-ellipsis">{prevId}</span>
                                                <span className="ml-2 text-xs bg-blue-200 px-1 rounded whitespace-nowrap">
                                                    {linkedMethods[index] || 'Unknown'}
                                                </span>
                                            </a>
                                        );
                                    }
                                });
                            }

                            return output.length > 0 ? output : <span className="text-gray-500 text-center text-sm">None</span>;
                        })()}
                    </div>
                </div>

                {/* Audio Player */}
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-2">Call Recording</h2>
                    <audio
                        controls
                        className="w-full"
                        onPlay={handlePlay}
                        src="data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
                    >
                        Your browser does not support the audio element.
                    </audio>
                </div>

                {/* Next Calls */}
                <div className="p-4">
                    <h2 className="text-xl font-semibold mb-2">Next Contacts</h2>
                    <div className="flex flex-col gap-2">
                        {(() => {
                            // Track processed IDs to avoid duplicates
                            const processedIds = new Set();
                            let output = [];

                            // First process detailed call records
                            if (contact.next_linked_calls_details && contact.next_linked_calls_details.length > 0) {
                                contact.next_linked_calls_details.forEach((nextCall, index) => {
                                    processedIds.add(nextCall.contact_id);
                                    output.push(
                                        <a
                                            key={`next-detail-${index}`}
                                            href={`/contact/${nextCall.contact_id}`}
                                            className="inline-flex items-center justify-between text-sm bg-green-100 hover:bg-green-200 text-green-800 px-3 py-2 rounded w-full"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-green-900">
                                                    {formatTimestamp(nextCall.call_date)}
                                                </span>
                                                <span className="text-xs text-green-700">
                                                    {formatTime(nextCall.duration)}
                                                </span>
                                            </div>
                                            <span className="ml-2 text-xs bg-green-200 px-1 rounded whitespace-nowrap">
                                                {nextCall.initiation_method}
                                            </span>
                                        </a>
                                    );
                                });
                            }

                            // Add specific next_contact_id if not already processed
                            if (contact.next_contact_id && !processedIds.has(contact.next_contact_id)) {
                                processedIds.add(contact.next_contact_id);
                                const hasDetails = contact.specific_next_contact_call_date &&
                                    contact.specific_next_contact_duration !== null &&
                                    contact.specific_next_contact_initiation_method;

                                if (hasDetails) {
                                    output.push(
                                        <a
                                            key="next-specific"
                                            href={`/contact/${contact.next_contact_id}`}
                                            className="inline-flex items-center justify-between text-sm bg-green-100 hover:bg-green-200 text-green-800 px-3 py-2 rounded w-full"
                                        >
                                            <div className="flex flex-col">
                                                <span className="text-green-900">
                                                    {formatTimestamp(contact.specific_next_contact_call_date)}
                                                </span>
                                                <span className="text-xs text-green-700">
                                                    {formatTime(contact.specific_next_contact_duration)}
                                                </span>
                                            </div>
                                            <span className="ml-2 text-xs bg-green-200 px-1 rounded whitespace-nowrap">
                                                {contact.specific_next_contact_initiation_method}
                                            </span>
                                        </a>
                                    );
                                } else {
                                    // External contact without details
                                    output.push(
                                        <div
                                            key="next-external"
                                            className="inline-flex items-center justify-between text-sm bg-gray-100 text-gray-500 px-3 py-2 rounded w-full cursor-text"
                                        >
                                            <span className="overflow-hidden text-ellipsis">
                                                {contact.next_contact_id}
                                            </span>
                                            <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">
                                                External
                                            </span>
                                        </div>
                                    );
                                }
                            }

                            // Legacy format as last resort
                            if (output.length === 0 && contact.next_linked_contact_ids) {
                                const linkedIds = contact.next_linked_contact_ids.split('|');
                                const linkedMethods = contact.next_linked_initiation_methods ?
                                    contact.next_linked_initiation_methods.split('|') : [];

                                linkedIds.forEach((nextId, index) => {
                                    if (!processedIds.has(nextId)) {
                                        processedIds.add(nextId);
                                        output.push(
                                            <a
                                                key={`next-legacy-${index}`}
                                                href={`/contact/${nextId}`}
                                                className="inline-flex items-center justify-between text-sm bg-green-100 hover:bg-green-200 text-green-800 px-3 py-2 rounded w-full"
                                            >
                                                <span className="overflow-hidden text-ellipsis">{nextId}</span>
                                                <span className="ml-2 text-xs bg-green-200 px-1 rounded whitespace-nowrap">
                                                    {linkedMethods[index] || 'Unknown'}
                                                </span>
                                            </a>
                                        );
                                    }
                                });
                            }

                            return output.length > 0 ? output : <span className="text-gray-500 text-center text-sm">None</span>;
                        })()}
                    </div>
                </div>
            </div>

            {/* Transcript */}
            {transcriptInfo && (

                <div
                    style={{ backgroundColor: "#fbfbfb" }}
                    className="bg-white shadow rounded-lg p-6 mt-8"
                    ref={transcriptRef}
                >
                    <h2 className="text-xl font-semibold mb-4">Transcript</h2>

                    {/* Enhanced view legend - only show when enhanced view exists */}
                    {contact.enhanced_view_json && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                            <h3 className="text-sm font-semibold mb-2">Enhanced View Legend:</h3>
                            <div className="flex flex-wrap gap-3">
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-purple-100 rounded mr-1"></div>
                                    <span className="text-xs">Intent Evidence</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-blue-100 rounded mr-1"></div>
                                    <span className="text-xs">Agent Performance</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-green-100 rounded mr-1"></div>
                                    <span className="text-xs">Customer Experience</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-yellow-100 rounded mr-1"></div>
                                    <span className="text-xs">Customer Satisfaction</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-red-100 rounded mr-1"></div>
                                    <span className="text-xs">Multiple Evidence</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="bg-gray-100 p-4 rounded-lg">
                        {processedTranscriptSections.length > 0 ? (
                            processedTranscriptSections.map((section, index) => {
                                if (section.type === 'normal') {
                                    return section.isEnhanced ? (
                                        // Enhanced view with highlighted sections
                                        <div key={index}>
                                            {renderEnhancedTranscript(section)}
                                        </div>
                                    ) : (
                                        // Regular view
                                        <div key={index} className="usan-transcript-box font-sans" dangerouslySetInnerHTML={{ __html: section.content }}>

                                        </div>
                                    );
                                } else { // overlap section
                                    return (
                                        <details key={index} className="my-4">
                                            <summary className="cursor-pointer p-2 bg-yellow-100 border border-yellow-300 rounded font-medium">
                                                Transfer Overlap Period - Click to expand/collapse
                                            </summary>
                                            <div className="p-4 bg-yellow-50 border-l border-r border-b border-yellow-300 whitespace-pre-wrap font-sans">
                                                {section.isEnhanced ? (
                                                    renderEnhancedTranscript({ ...section, content: section.content })
                                                ) : (
                                                    <div className="usan-transcript-box font-sans" dangerouslySetInnerHTML={{ __html: section.content }} />
                                                )}
                                            </div>
                                        </details>
                                    );
                                }
                            })
                        ) : (
                            <div>Processing transcript...</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ContactDetails;